import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Phone, Loader2, Navigation } from 'lucide-react';
import { ACTION_LABELS } from '@/components/operations/routeTaskCatalog';

const STOP_COLORS = { assigned: '#64748B', in_progress: '#1C4FC9', completed: '#5FA718', unable: '#C8641E' };
const NEXT = { assigned: 'in_progress', in_progress: 'completed' };

const TeamRoutes = ({ onBack }) => {
  const { token } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [routes, setRoutes] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState('');
  const [unableFor, setUnableFor] = useState(null);
  const [reason, setReason] = useState('');

  const load = () => axios.get(`${API}/routes?scope=mine`, h).then(r => setRoutes(r.data || [])).catch(() => setRoutes([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  const reloadActive = (id) => axios.get(`${API}/routes/${id}`, h).then(r => setActive(r.data)).catch(() => {});

  const patch = async (stop, body) => {
    setBusy(stop.stop_id);
    try { const r = await axios.patch(`${API}/routes/${active.id}/stops/${stop.stop_id}`, body, h); setActive(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setBusy('');
  };
  const toggleCheck = (stop, item) => patch(stop, { checklist: [{ id: item.id, done: !item.done }] });
  const toggleTask = (stop, item) => patch(stop, { tasks: [{ id: item.id, done: !item.done }] });
  const advance = (stop) => { const n = NEXT[stop.status]; if (n) patch(stop, { status: n }); };
  const submitUnable = async (stop) => { if (!reason.trim()) return toast.error('A reason is required'); await patch(stop, { status: 'unable', unable_reason: reason }); setUnableFor(null); setReason(''); };

  if (active) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-5" data-testid="team-route-detail">
        <button onClick={() => { setActive(null); load(); }} className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1C4FC9]"><ArrowLeft size={16} /> My Routes</button>
        <h2 className="text-xl font-bold text-[#0E2A47]">{active.route_number} {active.route_name && <span className="text-slate-400">· {active.route_name}</span>}</h2>
        <p className="text-sm text-slate-500">{active.date} · {active.progress.done}/{active.progress.total} stops done</p>
        <div className="mt-4 space-y-3">
          {active.stops.map(s => (
            <div key={s.stop_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`team-stop-${s.stop_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-bold text-[#0E2A47]">{s.order}. {s.location_name}</div>
                  <div className="text-xs text-slate-500">{s.address}</div></div>
                <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white capitalize" style={{ backgroundColor: STOP_COLORS[s.status] }}>{s.status.replace('_', ' ')}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs">
                {s.address && <a href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(s.address)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#1C4FC9]"><Navigation size={13} /> Directions</a>}
                {s.contact_phone && <a href={`tel:${s.contact_phone}`} className="inline-flex items-center gap-1 font-semibold text-[#1C4FC9]"><Phone size={13} /> {s.contact_name || 'Contact'}: {s.contact_phone}</a>}
              </div>
              {s.instructions && <div className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-700" data-testid={`team-stop-instr-${s.stop_id}`}>{s.instructions}</div>}
              {s.location_notes && <div className="mt-2 rounded-lg bg-amber-50 p-2 text-sm text-slate-700" data-testid={`team-stop-notes-${s.stop_id}`}><span className="font-semibold">Location notes:</span> {s.location_notes}</div>}
              {s.tasks?.length > 0 && (
                <div className="mt-2 space-y-2" data-testid={`team-stop-tasks-${s.stop_id}`}>
                  {Object.entries(s.tasks.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {})).map(([cat, items]) => (
                    <div key={cat}>
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{cat}</div>
                      {items.map(t => (
                        <label key={t.id} className="mt-1 flex items-start gap-2 text-sm text-slate-700" data-testid={`team-task-${t.id}`}>
                          <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(s, t)} disabled={s.status === 'completed' || s.status === 'unable'} className="mt-0.5" />
                          <span className="min-w-0 flex-1">
                            <span className={t.done ? 'text-slate-400 line-through' : ''}>{t.label}</span>
                            <span className="ml-1 rounded bg-[#1C4FC9]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#1C4FC9]">{ACTION_LABELS[t.action] || t.action}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {s.checklist?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {s.checklist.map(c => (
                    <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`team-check-${c.id}`}>
                      <input type="checkbox" checked={c.done} onChange={() => toggleCheck(s, c)} disabled={s.status === 'completed' || s.status === 'unable'} /> <span className={c.done ? 'line-through text-slate-400' : ''}>{c.text}</span>
                    </label>
                  ))}
                </div>
              )}
              {s.note && <div className="mt-1 text-xs italic text-slate-500">Note: {s.note}</div>}
              {s.unable_reason && <div className="mt-1 text-xs text-[#C8641E]">Unable: {s.unable_reason}</div>}
              {unableFor === s.stop_id ? (
                <div className="mt-2 space-y-2 rounded-lg border border-[#C8641E]/40 bg-orange-50 p-2">
                  <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required)" rows={2} data-testid={`team-unable-reason-${s.stop_id}`} className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
                  <div className="flex gap-2">
                    <button onClick={() => submitUnable(s)} data-testid={`team-unable-submit-${s.stop_id}`} className="rounded-lg bg-[#C8641E] px-3 py-1.5 text-xs font-semibold text-white">Confirm Unable</button>
                    <button onClick={() => { setUnableFor(null); setReason(''); }} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (s.status !== 'completed' && s.status !== 'unable') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => advance(s)} disabled={busy === s.stop_id} data-testid={`team-advance-${s.stop_id}`} className="rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">{busy === s.stop_id ? <Loader2 size={13} className="animate-spin" /> : (s.status === 'assigned' ? 'Start' : 'Complete')}</button>
                  <button onClick={() => patch(s, { note: window.prompt('Add a note', s.note || '') || s.note })} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Add note</button>
                  <button onClick={() => { setUnableFor(s.stop_id); setReason(s.unable_reason || ''); }} data-testid={`team-unable-${s.stop_id}`} className="rounded-lg border border-[#C8641E] px-3 py-1.5 text-xs font-semibold text-[#C8641E]">Unable to complete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-5" data-testid="team-routes">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1C4FC9]"><ArrowLeft size={16} /> Back</button>
      <h1 className="text-2xl font-extrabold text-[#0E2A47]">My Routes</h1>
      {routes === null ? <div className="mt-6 flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        : routes.length === 0 ? <p className="mt-6 text-sm text-slate-400" data-testid="team-routes-empty">No routes assigned to you.</p> : (
          <div className="mt-4 space-y-2">
            {routes.map(r => (
              <button key={r.id} onClick={() => reloadActive(r.id)} data-testid={`team-route-${r.id}`} className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-[#1C4FC9]">
                <div><div className="font-bold text-[#0E2A47]">{r.route_number} {r.route_name && <span className="text-slate-400">· {r.route_name}</span>}</div>
                  <div className="text-xs text-slate-500">{r.date} · {r.progress.done}/{r.progress.total} stops done</div></div>
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold text-white capitalize" style={{ backgroundColor: STOP_COLORS[r.status] }}>{r.status.replace('_', ' ')}</span>
              </button>
            ))}
          </div>
        )}
    </div>
  );
};

export default TeamRoutes;
